import { useContext, useState } from 'react';
import DOMPurify from 'dompurify';
import { CardsContext } from '../context/CardsContext';
import { SiteContentContext } from '../context/SiteContentContext';
import { Link } from 'react-router-dom';
import AutoCarousel from '../components/AutoCarousel';
import NewsCarousel from '../components/NewsCarousel';
import Icon from '../components/Icon';
import Mascot from '../components/Mascot';
import QuestionModal from '../components/QuestionModal';
import { injectIymcsMark } from '../utils/richText';
import { withIymcsMark } from '../components/IymcsMark';
import { useSeo, useJsonLd, toDescription, absUrl, BASE } from '../hooks/useSeo';
import '../styles/main_page.css';

const safe = (html) => ({ __html: injectIymcsMark(DOMPurify.sanitize(html || '')) });

const Main = ({ isPreview = false }) => {
  const { cards, productionCards } = useContext(CardsContext);
  const { content } = useContext(SiteContentContext);
  useSeo({
    title: content.heroTitle || null, // null → дефолтный «The Sailing Doctor — парусная школа»
    description: toDescription(content.aboutText) || 'Парусная школа The Sailing Doctor: обучение яхтингу, морские путешествия и регаты с профессиональными инструкторами. Сертификаты IYMCS.',
  });

  // Организация — на главной. Помогает Яндексу/Google показать карточку компании.
  useJsonLd({
    '@type': ['Organization', 'EducationalOrganization'],
    name: content.schoolName || 'The Sailing Doctor',
    url: BASE,
    logo: absUrl('/android-chrome-512x512.png'),
    description: toDescription(content.aboutText, 300) || 'Парусная школа: обучение яхтингу, морские путешествия и регаты.',
    email: content.email || undefined,
    telephone: content.phone || undefined,
    sameAs: [content.telegram, content.instagram].filter(Boolean),
  });

  const [isQuestionOpen, setIsQuestionOpen] = useState(false);
  const galleryImages = (content.mainGallery || []).filter(Boolean);

  const source = isPreview ? cards : productionCards;
  const visible = (items) => (items || []).filter(c => c.isPublished === true);

  const adventures = visible(source.adventures);
  const study = visible(source.study);
  const races = visible(source.races);
  const mainCards = visible(source.main);

  const sortedAdventures = [...adventures].sort((a, b) => new Date(a.startDate) - new Date(b.startDate));
  const sortedStudy = [...study].sort((a, b) => new Date(a.startDate) - new Date(b.startDate));
  const sortedRaces = [...races].sort((a, b) => new Date(a.startDate) - new Date(b.startDate));

  const TYPE_PATH = { adventures: 'adventures', study: 'study', races: 'races', main: 'news' };
  const cardLink = (card, type) => {
    const path = TYPE_PATH[type || card.type] || 'news';
    return isPreview ? `/preview/${path}/${card.slug || card.id}` : `/${path}/${card.slug || card.id}`;
  };
  const resolveRef = (cardRef) => {
    for (const [type, list] of Object.entries(source)) {
      const found = (list || []).find(c => String(c.id) === String(cardRef) || c.slug === String(cardRef));
      if (found) return cardLink(found, type);
    }
    return isPreview ? `/preview/news/${cardRef}` : `/news/${cardRef}`;
  };

  const heroStyle = content.heroImage
    ? { backgroundImage: `linear-gradient(rgba(0,0,0,0.45), rgba(0,0,0,0.45)), url(${content.heroImage})` }
    : {};

  return (
    <main className="main-page">
      <Mascot size={260} />

      {/* ===== HERO ===== */}
      <section className="main-hero" style={heroStyle}>
        <div className="main-hero-content">
          <h1>{content.heroTitle || 'Откройте для себя мир парусного спорта'}</h1>
          <p>{content.heroSubtitle || 'Обучение, путешествия и регаты с профессиональными инструкторами'}</p>
          <div className="main-hero-btns">
            <Link to={isPreview ? '/preview/schedule' : '/schedule'} className="main-hero-btn main-hero-btn--primary">
              Расписание
            </Link>
            <Link to={isPreview ? '/preview/info' : '/info'} className="main-hero-btn main-hero-btn--secondary">
              О школе
            </Link>
          </div>
        </div>
      </section>

      {/* ===== ТРИ НАПРАВЛЕНИЯ ===== */}
      <section className="main-directions">
        <div className="main-inner">
          <div className="direction-cards">
            <Link
              to={isPreview ? '/preview/study' : '/study'}
              className="direction-card"
              style={{ backgroundImage: sortedStudy[0]?.image ? `url(${sortedStudy[0].image})` : 'url(https://images.unsplash.com/photo-1530939027401-cca9d3f39f33?w=800&q=80)' }}
            >
              <div className="direction-card-overlay" />
              <div className="direction-card-body">
                <h2>{content.dirStudyTitle || 'Обучение яхтингу'}</h2>
                <p>{content.dirStudyDesc || 'Профессиональные курсы для начинающих и опытных яхтсменов. Теория и практика под руководством сертифицированных инструкторов.'}</p>
                <span className="direction-card-btn">{content.dirStudyBtn || 'Ближайший курс →'}</span>
              </div>
            </Link>
            <Link
              to={isPreview ? '/preview/races' : '/races'}
              className="direction-card"
              style={{ backgroundImage: sortedRaces[0]?.image ? `url(${sortedRaces[0].image})` : 'url(https://images.unsplash.com/photo-1561128295-a24f0da38e4f?w=800&q=80)' }}
            >
              <div className="direction-card-overlay" />
              <div className="direction-card-body">
                <h2>{content.dirRacesTitle || 'Соревнования'}</h2>
                <p>{content.dirRacesDesc || 'Участвуйте в регатах различного уровня — от любительских до профессиональных. Подготовка экипажей и аренда яхт.'}</p>
                <span className="direction-card-btn">{content.dirRacesBtn || 'Ближайшая регата →'}</span>
              </div>
            </Link>
            <Link
              to={isPreview ? '/preview/adventures' : '/adventures'}
              className="direction-card"
              style={{ backgroundImage: sortedAdventures[0]?.image ? `url(${sortedAdventures[0].image})` : 'url(https://images.unsplash.com/photo-1502680390469-be75c86b636f?w=800&q=80)' }}
            >
              <div className="direction-card-overlay" />
              <div className="direction-card-body">
                <h2>{content.dirAdventuresTitle || 'Путешествия'}</h2>
                <p>{content.dirAdventuresDesc || 'Организуем парусные путешествия по самым красивым местам. От однодневных прогулок до многодневных круизов.'}</p>
                <span className="direction-card-btn">{content.dirAdventuresBtn || 'Ближайшее путешествие →'}</span>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* ===== БЛОК ФИЧ ===== */}
      {(content.mainFeaturesTitle || (content.mainFeatures || []).length > 0) && (
        <section className="main-features">
          <div className="main-inner" style={{ textAlign: 'center' }}>
            {content.mainFeaturesTitle && <h2>{content.mainFeaturesTitle}</h2>}
            {content.mainFeaturesText && <p className="main-features-lead">{content.mainFeaturesText}</p>}
            <div className="main-features-grid">
              {(content.mainFeatures || []).map(f => (
                <div key={f.id} className="main-feature-item">
                  <Icon name={f.icon} size={40} />
                  <h3>{f.title}</h3>
                  <p>{f.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ===== НОВОСТИ ===== */}
      {mainCards.length > 0 && (
        <section className="main-news">
          <div className="main-inner">
            <h2>Новости и акции</h2>
            <NewsCarousel>
              {mainCards.map(card => {
                const to = card.cardRef ? resolveRef(card.cardRef) : cardLink(card, 'main');
                return (
                  <Link key={card.id} to={to} className="card-link">
                    <div className="card">
                      {card.image && (
                        <div className="card-img" style={{ backgroundImage: `url(${card.image})` }}>
                          {card.badge && <span className="card-badge">{card.badge}</span>}
                        </div>
                      )}
                      <div className="card-body">
                        <h3>{card.title}</h3>
                        <div dangerouslySetInnerHTML={safe(card.description)} />
                      </div>
                    </div>
                  </Link>
                );
              })}
            </NewsCarousel>
          </div>
        </section>
      )}

      {/* ===== ПОЧЕМУ МЫ ===== */}
      {(() => {
        const whyTitle = content.whyTitle || 'Почему выбирают нашу школу';
        const advantages = content.advantages || [
          { icon: 'anchor',    title: 'Опытные инструкторы',  text: '10+ лет практики и международные сертификаты' },
          { icon: 'shield',    title: 'Безопасность',         text: 'Современные яхты с полным комплектом снаряжения' },
          { icon: 'compass',   title: 'Уникальные маршруты',  text: 'Закрытые бухты и малоизвестные острова' },
          { icon: 'handshake', title: 'Индивидуальный подход',text: 'Программы под ваш уровень и цели' },
        ];
        return (
          <section className="main-why">
            <div className="main-inner">
              <h2>{whyTitle}</h2>
              <p className="main-why-lead">{content.aboutText || 'Мы — команда профессиональных яхтсменов, которая открывает для вас мир парусов.'}</p>
              <div className="advantages-grid">
                {advantages.map((a, i) => {
                  const inner = (
                    <>
                      <Icon name={a.icon || 'anchor'} size={44}/>
                      <h3>{withIymcsMark(a.title)}</h3>
                      <div dangerouslySetInnerHTML={safe(a.text)} />
                    </>
                  );
                  return a.link ? (
                    <Link key={i} to={isPreview && !a.link.startsWith('http') ? `/preview${a.link}` : a.link} className="advantage advantage-link">
                      {inner}
                    </Link>
                  ) : (
                    <div key={i} className="advantage">{inner}</div>
                  );
                })}
              </div>
            </div>
          </section>
        );
      })()}

      {/* ===== ГАЛЕРЕЯ ===== */}
      {galleryImages.length > 0 && (
        <AutoCarousel images={galleryImages} title="Наши фотографии" interval={4000} />
      )}

      <section className="cta-section">
        <h2>Остались вопросы?</h2>
        <p>Напишите нам — поможем выбрать программу и расскажем о датах. Ответим в течение дня.</p>
        <button className="cta-button" onClick={() => setIsQuestionOpen(true)}>Задать вопрос</button>
      </section>
      <QuestionModal isOpen={isQuestionOpen} onClose={() => setIsQuestionOpen(false)} pageTitle="Главная" />

    </main>
  );
};

export default Main;
